import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { demoReducer, initialState } from './demoReducer';
import connectorsData from '../mock/connectors.json';
import assetsData from '../mock/assets.json';
import borrowingBaseData from '../mock/borrowingBase.json';
import offersData from '../mock/offers.json';
import agentsData from '../mock/agents.json';
import propertiesData from '../mock/properties.json';
import listingsData from '../mock/listings.json';
import investorsData from '../mock/investors.json';
import commitmentsData from '../mock/commitments.json';

const DemoContext = createContext();

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
};

export const DemoProvider = ({ children }) => {
  const [state, dispatch] = useReducer(demoReducer, initialState);

  // Load initial mock data
  useEffect(() => {
    dispatch({ type: 'LOAD_CONNECTORS', payload: connectorsData.connectors });
    dispatch({ type: 'LOAD_ASSETS', payload: assetsData.assets });
    dispatch({ type: 'LOAD_BORROWING_BASE', payload: borrowingBaseData.borrowingBase });
    dispatch({ type: 'LOAD_OFFERS', payload: offersData.offers });
    dispatch({ type: 'LOAD_AGENTS', payload: agentsData.agents });
    dispatch({ type: 'LOAD_PROPERTIES', payload: propertiesData.properties });
    dispatch({ type: 'LOAD_LISTINGS', payload: listingsData.listings });
    dispatch({ type: 'LOAD_INVESTORS', payload: investorsData.investors });
    dispatch({ type: 'LOAD_COMMITMENTS', payload: commitmentsData.commitments });
  }, []);

  // Actions
  const actions = {
    // Connector actions
    connectSystem: (connectorId) => {
      dispatch({ type: 'CONNECT_SYSTEM', payload: connectorId });
    },
    disconnectSystem: (connectorId) => {
      dispatch({ type: 'DISCONNECT_SYSTEM', payload: connectorId });
    },

    // Agent actions
    runAgent: (agentId) => {
      dispatch({ type: 'RUN_AGENT', payload: agentId });
    },

    // Offer actions
    selectOffer: (offerId) => {
      dispatch({ type: 'SELECT_OFFER', payload: offerId });
    },
    acceptOffer: (offerId) => {
      dispatch({ type: 'ACCEPT_OFFER', payload: offerId });
    },

    // Borrowing base actions
    drawFunds: (amount) => {
      dispatch({ type: 'DRAW_FUNDS', payload: amount });
    },

    // Marketplace actions
    createListing: (listing) => {
      dispatch({ type: 'CREATE_LISTING', payload: listing });
    },
    updateListing: (listing) => {
      dispatch({ type: 'UPDATE_LISTING', payload: listing });
    },
    commitToListing: (commitment) => {
      dispatch({ type: 'COMMIT_TO_LISTING', payload: commitment });
    },
    signupInvestor: (investor) => {
      dispatch({ type: 'SIGNUP_INVESTOR', payload: investor });
    },
    updateInvestorMandates: (investorId, mandates) => {
      dispatch({ type: 'UPDATE_INVESTOR_MANDATES', payload: { id: investorId, mandates } });
    },
    updateMarketplaceFilters: (filters) => {
      dispatch({ type: 'UPDATE_MARKETPLACE_FILTERS', payload: filters });
    },
    runEncumbranceAgent: () => {
      dispatch({ type: 'RUN_ENCUMBRANCE_AGENT' });
    },

    // UI actions
    showToast: (message, type = 'success') => {
      dispatch({ type: 'SHOW_TOAST', payload: { message, type } });
    },
    hideToast: () => {
      dispatch({ type: 'HIDE_TOAST' });
    },
    showModal: (modalType, data = {}) => {
      dispatch({ type: 'SHOW_MODAL', payload: { modalType, data } });
    },
    hideModal: () => {
      dispatch({ type: 'HIDE_MODAL' });
    }
  };

  const value = {
    state,
    actions
  };

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};
